import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Loader, AnalyticsProvider } from '@resy/ui-kitchen';
import AccountPageRoutes from './AccountPageRoutes';
import AmexMarketingProvider from './AmexMarketing/AmexMarketingProvider';
import AmexCenturionNY from '../AmexCenturionNY';
import {
  AmexGDADirOutlet, AmexGDAOutlet, AmexDynamicTermsOutlet, AmexShopSmallOutlet,
  AmexExperientialOutlet, AmexGDAB2BOutlet, AmexSecurityCenterOutlet, AmexResyCreditOutlet, AmexResyValueOutlet, AmexPlatinumNightsOutlet,
  ResyRewardsOutlet,
} from './AmexMarketing/AmexMarketingChildren';
import MultipleListsSharedContainer from '../../containers/MultipleListsSharedContainer/MultipleListsSharedContainer';
import SurveyContainer from '../../containers/SurveyContainer';

const ResyStaticWrapper = lazy(() => import('./ResyStaticWrapper'));
const About = lazy(() => import('../About'));
const Careers = lazy(() => import('../Careers'));
const TermsOfService = lazy(() => import('../TermsOfService'));
const TermsOfServiceResyPay = lazy(() => import('../TermsOfServiceResyPay'));
const PrivacyPolicy = lazy(() => import('../PrivacyPolicy'));
const CookiePolicy = lazy(() => import('../CookiePolicy'));
const Dpa = lazy(() => import('../Dpa'));
const GdaOnboarding = lazy(() => import('../GdaOnboarding'));
const AccessibilityStatement = lazy(() => import('../AccessibilityStatement'));
const SuspenseWrapper = (page) => (<Suspense fallback={<Loader />}>{page}</Suspense>);

const ResyRoutes = (props) => {
  const {
    $location,
    $rootScope,
    $routeParams,
    $window,
    Analytics,
    Config,
    ResyAmexConfig,
  } = props;

  const getAmexPlatinumNightsOutlet = () => (
    <AmexPlatinumNightsOutlet
      $location={$location}
      $rootScope={$rootScope}
      $routeParams={$routeParams}
      Analytics={Analytics}
      Config={Config}
      ResyAmexConfig={ResyAmexConfig}
    />
  );

  const getAmexResyCreditOutlet = () => (
    <AmexResyCreditOutlet
      $location={$location}
      $rootScope={$rootScope}
      $routeParams={$routeParams}
      Analytics={Analytics}
      Config={Config}
      ResyAmexConfig={ResyAmexConfig}
    />
  );

  return (
    <AnalyticsProvider Analytics={Analytics}>
      <Routes>
        <Route element={(SuspenseWrapper(<ResyStaticWrapper $rootScope={$rootScope} $window={$window} />))}>
          <Route path="/about-and-help" element={<About Analytics={Analytics} />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/terms/resypay" element={<TermsOfServiceResyPay />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy $rootScope={$rootScope} />} />
          <Route path="/dpa" element={<Dpa />} />
          <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
        </Route>
        <Route
          path="/global-dining-access-onboarding/:key"
          element={(
            SuspenseWrapper(
              <GdaOnboarding
                hideCta
              />
            )
          )}
        />

        <Route path="/account/*" element={<AccountPageRoutes {...props} />} />
        <Route path="/survey/*" element={<SurveyContainer {...props} />} />
        <Route
          path="/shared/lists/:guid"
          element={(
            <MultipleListsSharedContainer
              $rootScope={$rootScope}
              $routeParams={$routeParams}
              Analytics={Analytics}
              context="resy.com"
            />
          )}
        />

        <Route element={(
          <AmexMarketingProvider
            $rootScope={$rootScope}
            Analytics={Analytics}
            $location={$location}
          />
        )}
        >
          <Route
            path="/global-dining-access/directory"
            element={(
              <AmexGDADirOutlet
                $location={$location}
                $rootScope={$rootScope}
                Analytics={Analytics}
                ResyAmexConfig={ResyAmexConfig}
              />
            )}
          />
          <Route
            path="/global-dining-access-member"
            element={(
              <AmexGDAOutlet
                $location={$location}
                $rootScope={$rootScope}
                matchMedia={$rootScope.matchMedia}
                Analytics={Analytics}
                Config={Config}
                ResyAmexConfig={ResyAmexConfig}
              />
            )}
          />
          <Route
            path="/global-dining-access"
            element={(
              <AmexGDAOutlet
                $location={$location}
                $rootScope={$rootScope}
                matchMedia={$rootScope.matchMedia}
                Analytics={Analytics}
                Config={Config}
                ResyAmexConfig={ResyAmexConfig}
              />
            )}
          />
          <Route
            path="/cnyevents"
            element={(
              <AmexCenturionNY
                $rootScope={$rootScope}
                Analytics={Analytics}
                ResyAmexConfig={ResyAmexConfig}
                $location={$location}
              />
            )}
          />
          <Route
            path="/dynamicterms/:initiativeid"
            element={(
              <AmexDynamicTermsOutlet
                $location={$location}
                $rootScope={$rootScope}
                Analytics={Analytics}
                ResyAmexConfig={ResyAmexConfig}
              />
            )}
          />
          <Route
            path="/shopsmall/"
            element={(
              <AmexShopSmallOutlet
                $rootScope={$rootScope}
                $location={$location}
                Analytics={Analytics}
                ResyAmexConfig={ResyAmexConfig}
                $routeParams={$routeParams}
                $window={$window}
              />
            )}
          />
          <Route
            path="/experiences/"
            element={(
              <AmexExperientialOutlet
                $rootScope={$rootScope}
                $location={$location}
                Analytics={Analytics}
                ResyAmexConfig={ResyAmexConfig}
                $routeParams={$routeParams}
                $window={$window}
                matchMedia={$rootScope.matchMedia}
              />
            )}
          />

          <Route
            path="/global-dining-access-partner-page/"
            element={(
              <AmexGDAB2BOutlet
                $rootScope={$rootScope}
                $location={$location}
                $window={$window}
                Analytics={Analytics}
                ResyAmexConfig={ResyAmexConfig}
                $routeParams={$routeParams}
              />
            )}
          />
          <Route
            path="/security-center"
            element={(
              <AmexSecurityCenterOutlet
                $location={$location}
                $rootScope={$rootScope}
                $routeParams={$routeParams}
                Analytics={Analytics}
                Config={Config}
                ResyAmexConfig={ResyAmexConfig}
              />
            )}
          />
          <Route
            path="/resycredit"
            element={getAmexResyCreditOutlet()}
          />
          <Route
            path="/resycredit/amexgold"
            element={getAmexResyCreditOutlet()}
          />
          <Route
            path="/resycredit/deltaskymilesreserve"
            element={getAmexResyCreditOutlet()}
          />
          <Route
            path="/resycredit/deltaskymilesplatinum"
            element={getAmexResyCreditOutlet()}
          />
          <Route
            path="/resycredit/platinumcard"
            element={getAmexResyCreditOutlet()}
          />
          <Route
            path="/welcome"
            element={(
              <AmexResyValueOutlet
                $location={$location}
                $rootScope={$rootScope}
                $routeParams={$routeParams}
                Analytics={Analytics}
                Config={Config}
                ResyAmexConfig={ResyAmexConfig}
              />
            )}
          />
          <Route
            path="/welcomecardmember"
            element={(
              <AmexResyValueOutlet
                $location={$location}
                $rootScope={$rootScope}
                $routeParams={$routeParams}
                Analytics={Analytics}
                Config={Config}
                ResyAmexConfig={ResyAmexConfig}
              />
            )}
          />
          <Route
            path="/specialaccess"
            element={getAmexPlatinumNightsOutlet()}
          />
          <Route
            path="/platinum"
            element={getAmexPlatinumNightsOutlet()}
          />
          <Route
            path="/platinum-member"
            element={getAmexPlatinumNightsOutlet()}
          />
          <Route
            path="/amex-platinum"
            element={getAmexPlatinumNightsOutlet()}
          />
          <Route
            path="/resy-rewards"
            element={(
              <ResyRewardsOutlet
                $location={$location}
                $rootScope={$rootScope}
                $routeParams={$routeParams}
                Analytics={Analytics}
                ResyAmexConfig={ResyAmexConfig}
              />
            )}
          />
        </Route>

        <Route path="/nps2022q4terms" element={<Navigate to="/dynamicterms/nps2022q4terms" replace />} />
        <Route path="/nps2023q2terms" element={<Navigate to="/dynamicterms/nps2023q2terms" replace />} />
        <Route path="/shop-small" element={<Navigate to="/shopsmall" replace />} />
      </Routes>
    </AnalyticsProvider>
  );
};

ResyRoutes.propTypes = {
  $location: PropTypes.shape({
    search: PropTypes.func.isRequired,
    path: PropTypes.func.isRequired,
  }).isRequired,
  $rootScope: PropTypes.shape({
    $digest: PropTypes.func,
    reduxStore: PropTypes.shape({}),
    matchMedia: PropTypes.shape({
      xs: PropTypes.bool,
      sm: PropTypes.bool,
      md: PropTypes.bool,
      lg: PropTypes.bool,
      ipadLandscape: PropTypes.bool,
      xl: PropTypes.bool,
    }),
  }).isRequired,
  $window: PropTypes.shape({
    parent: PropTypes.shape({
      postMessage: PropTypes.func,
    }),
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  Config: PropTypes.shape({
    baseRequestUrl: PropTypes.string.isRequired,
    origin: PropTypes.string.isRequired,
  }).isRequired,
  ResyAmexConfig: PropTypes.shape({
    baseRequestUrl: PropTypes.string.isRequired,
  }).isRequired,
  $routeParams: PropTypes.shape({
    date: PropTypes.string.isRequired,
  }).isRequired,

};

export default ResyRoutes;
